import { VENUE_DIRECTORY, waLink, WA_MSG, IMG } from "../data/content";
import { PageHero, Reveal, Btn, useTitle } from "../components/shared";
import { WhatsAppIcon } from "../components/Navbar";
import { Link } from "react-router-dom";
import { MapPin, Users, UtensilsCrossed, Sparkles } from "lucide-react";

const Stars = ({ rating }) => (
  <span className="relative inline-flex align-middle" data-testid="venue-stars">
    <span className="text-[13px] tracking-tight text-stone2/25">★★★★★</span>
    <span className="absolute inset-0 overflow-hidden text-[13px] tracking-tight text-gold" style={{ width: `${(rating / 5) * 100}%` }}>
      ★★★★★
    </span>
  </span>
);

function VenueCard({ v, city, i }) {
  return (
    <Reveal delay={(i % 3) * 0.08}>
      <article className="group flex h-full flex-col border border-gold/25 bg-white/70 transition-all duration-500 hover:border-gold/60 hover:shadow-[0_24px_60px_rgba(197,160,89,0.18)]" data-testid={`venue-card-${city.toLowerCase()}-${i}`}>
        <div className="relative overflow-hidden">
          <img src={v.img} alt={v.name} loading="lazy" className="h-60 w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
          <span className="absolute left-4 top-4 bg-ivory/90 px-3.5 py-1.5 text-[9px] uppercase tracking-[0.2em] text-emerald">{v.type}</span>
          {v.tag && <span className="absolute right-4 top-4 bg-emerald/90 px-3.5 py-1.5 text-[9px] uppercase tracking-[0.18em] text-gold-light">{v.tag}</span>}
        </div>

        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-serif text-2xl leading-tight text-emerald">{v.name}</h3>

          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-stone2">
            <Stars rating={v.stars} />
            <span className="font-medium text-gold-text">{v.stars.toFixed(1)}</span>
            <span className="text-stone2/60">· Google Reviews</span>
          </div>

          <p className="mt-2.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-stone2">
            <MapPin className="h-3.5 w-3.5 text-gold" /> {v.location}
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3 border-t border-gold/20 pt-4">
            <p className="flex items-start gap-2 text-xs text-stone2">
              <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> {v.capacity}
            </p>
            <p className="flex items-start gap-2 text-xs text-stone2">
              <UtensilsCrossed className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
              {v.price ? <span className="font-serif text-base text-gold-text">{v.price}</span> : "Price on request"}
            </p>
          </div>

          {v.inclusions.length > 0 && (
            <div className="mt-4" data-testid={`venue-inclusions-${city.toLowerCase()}-${i}`}>
              <p className="flex items-center gap-2 text-[9px] uppercase tracking-[0.22em] text-gold-text">
                <Sparkles className="h-3 w-3" /> Key Inclusions
              </p>
              <ul className="mt-2 grid grid-cols-1 gap-1">
                {v.inclusions.map((x) => (
                  <li key={x} className="flex items-center gap-2 text-xs text-stone2">
                    <span className="h-1 w-1 rotate-45 bg-gold" /> {x}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-auto pt-5">
            <Link to="/enquire" data-testid={`venue-enquire-${city.toLowerCase()}-${i}`} className="inline-flex w-max items-center gap-3 border-b border-gold pb-1 text-[11px] font-medium uppercase tracking-[0.25em] text-emerald transition-colors hover:text-gold-text">
              Enquire <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function Venues() {
  useTitle("Wedding Venues | The Big Wedd — Goa · Jaipur · Udaipur · Rishikesh · Shimla");
  return (
    <main>
      <PageHero
        eyebrow="Venues"
        title={[<span key="t">Our venue <span className="italic text-gold-light">collection</span></span>]}
        sub="A hand-picked directory of the finest wedding venues across Goa, Jaipur, Udaipur, Rishikesh and Shimla — with ratings, capacities and per-plate pricing."
        img={IMG.venuesHero}
      />

      {VENUE_DIRECTORY.map((group) => (
        <section key={group.city} className="bg-ivory py-16 md:py-20" data-testid={`venue-section-${group.city.toLowerCase()}`}>
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Reveal>
              <div className="flex flex-col justify-between gap-3 border-b border-gold/25 pb-6 md:flex-row md:items-end">
                <div>
                  <h2 className="font-serif text-4xl md:text-5xl text-emerald leading-none">{group.city}</h2>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-gold-text">{group.tagline}</p>
                </div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-stone2/60">{group.venues.length} venues</p>
              </div>
            </Reveal>
            <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {group.venues.map((v, i) => (
                <VenueCard key={v.name} v={v} city={group.city} i={i} />
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="bg-sand py-20 md:py-24" data-testid="venues-cta">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl text-emerald leading-[1.05]">
              Can't decide? <span className="italic text-gold-text">We'll shortlist for you.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-sm md:text-base leading-relaxed text-stone2">
              Tell us your dates, guest count and budget — we'll recommend the venues that fit best, negotiate on your behalf and handle everything from site visits to bookings.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Btn to="/enquire" testid="venues-cta">Plan My Wedding</Btn>
              <a href={waLink(WA_MSG)} target="_blank" rel="noopener noreferrer" data-testid="venues-cta-whatsapp" className="inline-flex items-center gap-3 rounded-full border border-gold/60 px-8 py-4 text-[11px] md:text-xs font-medium uppercase tracking-[0.22em] text-emerald transition-all duration-500 hover:bg-gold hover:text-ink">
                <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
